import React, { useCallback, useEffect, useState } from "react";
import { Button, Input, Message, Select } from "semantic-ui-react";
import DataTable from "../dataTable";
import Report from "../../../models/Report";

const earliestDate = "2020-04-01";
const membershipTypes = [
    { key: "MASHIE_FOUNDER_MEMBERSHIP", text: "Founder" },
    { key: "MASHIE_CLUB_MEMBERSHIP", text: "Club" },
    { key: "MASHIE_LINKS_MEMBERSHIP", text: "Links" },
    { key: "MASHIE_COUNTRY_MEMBERSHIP", text: "Country" },
    { key: "MASHIE_TRIAL_MEMBERSHIP", text: "Trial" },
];
const allMembershipTypes = membershipTypes.map(({ key }) => key);

const londonDateValue = () => {
    const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Europe/London",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    })
        .formatToParts(new Date())
        .reduce((carry, part) => {
            carry[part.type] = part.value;
            return carry;
        }, {});

    return `${parts.year}-${parts.month}-${parts.day}`;
};

const previousYearDateValue = (value) => {
    const [year, month, day] = value.split("-").map(Number);
    const previousYear = new Date(Date.UTC(year - 1, month - 1, day));
    if (previousYear.getUTCMonth() !== month - 1) {
        previousYear.setUTCDate(0);
    }

    return [
        previousYear.getUTCFullYear(),
        String(previousYear.getUTCMonth() + 1).padStart(2, "0"),
        String(previousYear.getUTCDate()).padStart(2, "0"),
    ].join("-");
};

const today = londonDateValue();
const initialFilters = {
    startDate: previousYearDateValue(today),
    endDate: today,
    membershipTypes: allMembershipTypes,
};
const percentageText = (value) => `${Number(value || 0).toFixed(1)}%`;
const requestParams = (selectedFilters) => {
    const params = {
        startDate: selectedFilters.startDate,
        endDate: selectedFilters.endDate,
    };
    if (
        selectedFilters.membershipTypes.length !== allMembershipTypes.length
    ) {
        params.membershipTypes = selectedFilters.membershipTypes.join(",");
    }
    return params;
};

export default function TeeTimeRequestBookingsByHour() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [error, setError] = useState(false);
    const [filters, setFilters] = useState(initialFilters);

    const loadReport = useCallback(async (selectedFilters) => {
        setLoading(true);
        setError(false);
        try {
            const reportData = await Report.teeTimeRequestBookingsByHour(
                requestParams(selectedFilters),
            );
            setData(reportData);
        } catch (exception) {
            setError(true);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        loadReport(initialFilters);
    }, [loadReport]);

    const showAllTime = () => {
        const nextFilters = {
            ...filters,
            startDate: earliestDate,
            endDate: today,
        };
        setFilters(nextFilters);
        loadReport(nextFilters);
    };

    const headers = [
        { key: "hourBlock", text: "Hour block" },
        { key: "total", text: "Total" },
        { key: "percentage", text: "Percentage" },
        { key: "booked", text: "Booked" },
        { key: "bookedPercentage", text: "Booked %" },
        { key: "cancelled", text: "Cancelled" },
        { key: "cancelledPercentage", text: "Cancelled %" },
        { key: "other", text: "Other" },
        { key: "otherPercentage", text: "Other %" },
    ];
    const tableData = data.map((item) => ({
        hourBlock: { value: item.hourBlock, search: item.hour },
        total: { value: item.total, search: item.total },
        percentage: {
            value: percentageText(item.percentage),
            search: item.percentage,
        },
        booked: { value: item.booked, search: item.booked },
        bookedPercentage: {
            value: percentageText(item.bookedPercentage),
            search: item.bookedPercentage,
        },
        cancelled: { value: item.cancelled, search: item.cancelled },
        cancelledPercentage: {
            value: percentageText(item.cancelledPercentage),
            search: item.cancelledPercentage,
        },
        other: { value: item.other, search: item.other },
        otherPercentage: {
            value: percentageText(item.otherPercentage),
            search: item.otherPercentage,
        },
    }));

    return (
        <>
            <p>
                Non-deleted tee-time requests grouped by the UK-local hour in
                which they were created.
            </p>

            <div className='row mb-3'>
                <div className='col-sm-12 col-md-3 mb-2'>
                    <label className='with-input'>Start date</label>
                    <Input
                        fluid
                        type='date'
                        min={earliestDate}
                        max={today}
                        value={filters.startDate}
                        onChange={({ target }) =>
                            setFilters({ ...filters, startDate: target.value })
                        }
                    />
                </div>
                <div className='col-sm-12 col-md-3 mb-2'>
                    <label className='with-input'>End date</label>
                    <Input
                        fluid
                        type='date'
                        min={earliestDate}
                        max={today}
                        value={filters.endDate}
                        onChange={({ target }) =>
                            setFilters({ ...filters, endDate: target.value })
                        }
                    />
                </div>
                <div className='col-sm-12 col-md-6 mb-2'>
                    <label className='with-input'>Current membership type</label>
                    <Select
                        fluid
                        search
                        multiple
                        placeholder='Select membership types'
                        value={filters.membershipTypes}
                        options={membershipTypes.map(({ key, text }) => ({
                            key,
                            text,
                            value: key,
                        }))}
                        onChange={(_, { value }) =>
                            setFilters({ ...filters, membershipTypes: value })
                        }
                    />
                </div>
            </div>

            <div className='mb-3'>
                <Button
                    primary
                    className='theme'
                    onClick={() => loadReport(filters)}
                >
                    Apply filters
                </Button>
                <Button onClick={showAllTime}>All time</Button>
            </div>

            {error && (
                <Message negative>
                    Failed to load the report. Check the selected filters and
                    try again.
                </Message>
            )}

            <DataTable
                loading={loading}
                headers={headers}
                data={tableData}
                hideSearch
                quickSort={false}
            />
        </>
    );
}
